import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CartItem, Product } from '../types';

interface CartContextType {
  items: CartItem[];
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addToCart: (product: Product, quantity: number, size?: string, color?: string) => void;
  removeFromCart: (uniqueId: string) => void;
  updateQuantity: (uniqueId: string, delta: number) => void;
  clearCart: () => void;
  cartCount: number;
  subtotal: number;
  checkout: (stripeMode?: 'sandbox' | 'production', walletAddress?: string) => Promise<void>;
  isCheckingOut: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const savedCart = localStorage.getItem('4hvnsk8_cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (e) {
      console.error('Failed to parse cart', e);
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Persist to local storage
  useEffect(() => {
    localStorage.setItem('4hvnsk8_cart', JSON.stringify(items));
  }, [items]);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const addToCart = (product: Product, quantity: number, size?: string, color?: string) => {
    setItems((prev) => {
      const uniqueId = [product.id, size, color].filter(Boolean).join('-');
      const existingItem = prev.find((item) => item.uniqueId === uniqueId);

      if (existingItem) {
        return prev.map((item) =>
          item.uniqueId === uniqueId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...prev, { ...product, quantity, selectedSize: size, selectedColor: color, uniqueId }];
    });
    openCart();
  };

  const removeFromCart = (uniqueId: string) => {
    setItems((prev) => prev.filter((item) => item.uniqueId !== uniqueId));
  };
  
  const clearCart = () => {
    setItems([]);
  };

  const updateQuantity = (uniqueId: string, delta: number) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.uniqueId === uniqueId) {
          const newQuantity = Math.max(1, item.quantity + delta);
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const checkout = async (stripeMode: 'sandbox' | 'production' = 'sandbox', walletAddress?: string) => {
    try {
        setIsCheckingOut(true);
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../utils/firebase');
        const { getStripeForMode } = await import('../utils/stripe');

        const createSession = httpsCallable(functions, 'createCheckoutSession');

        // Call Backend with stripe mode and optional wallet address for NFT minting
        const { data }: any = await createSession({
            cart: items,
            successUrl: `${window.location.origin}/success`,
            cancelUrl: `${window.location.origin}/cancel`,
            stripeMode: stripeMode,
            ...(walletAddress && { walletAddress })
        });

        if (data.url) {
            window.location.href = data.url;
        } else {
             // Fallback if no URL returned (shouldn't happen with correct Stripe API version)
            const stripe = await getStripeForMode(stripeMode);
            if (stripe) {
                const { error } = await (stripe as any).redirectToCheckout({ sessionId: data.id });
                if (error) {
                    console.error('Stripe Redirect Error:', error);
                    alert('Payment redirect failed. Please try again.');
                }
            }
        }
    } catch (error) {
        console.error('Checkout Error:', error);
        alert('Checkout failed. Please try again later.');
    } finally {
        setIsCheckingOut(false);
    }
  };

  const cartCount = items.reduce((total, item) => total + item.quantity, 0);
  const subtotal = items.reduce((total, item) => total + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        isCartOpen,
        openCart,
        closeCart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
        subtotal,
        checkout,
        isCheckingOut
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};